import { Search } from "lucide-react";
import React, { useState, useMemo, useEffect, useCallback } from "react";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { normalizeFacilityValue } from "./GoogleMapsUtils";

// Icon configs (must match those in RMMap.js / googleMapsUtils.js)
const disasterIconConfigs = {
  FLOOD: { color: "#3B82F6", icon: "💧", label: "Flood" },
  Flood: { color: "#3B82F6", icon: "💧", label: "Flood" },
  COLD_WAVE: { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
  "Cold Wave": { color: "#6B7280", icon: "❄️", label: "Cold Wave" },
  EARTHQUAKE: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
  Earthquake: { color: "#8B5CF6", icon: "🌍", label: "Earthquake" },
  Fire: { color: "#DC2626", icon: "🔥", label: "Fire" },
  Cyclone: { color: "#F59E0B", icon: "🌀", label: "Cyclone" },
  "Heat Wave": { color: "#F97316", icon: "🔥", label: "Heat Wave" },
  HEAT_WAVE: { color: "#F97316", icon: "🔥", label: "Heat Wave" },
  DEFAULT: { color: "#6B7280", icon: "⚠️", label: "Incident" },
  AVALANCHE: { color: "#9333EA", icon: "🏔️", label: "Avalanche" },
  Avalanche: { color: "#9333EA", icon: "🏔️", label: "Avalanche" },
  "Cloud Burst": { color: "#0284C7", icon: "⛈️", label: "Cloud Burst", whiteBg: true },
  CLOUD_BURST: { color: "#0284C7", icon: "⛈️", label: "Cloud Burst", whiteBg: true },
};

const supportPointIconConfigs = {
  ShelterHome: { color: "#3b82f6", emoji: "🏠", label: "Shelter Home" },
  BlanketDistribution: { color: "#8b5cf6", emoji: "🧣", label: "Blanket Distribution" },
  MedicalCamp: { color: "#10b981", emoji: "⛺", label: "Medical Camp" },
  Hospital: { color: "#2563eb", emoji: "🏥", label: "Hospital" },
  CollapsedBuildingZone: { color: "#dc2626", emoji: "🏚️", label: "Collapsed Building Zone" },
  TemporaryShelter: { color: "#f59e0b", emoji: "⛺", label: "Temporary Shelter" },
  RescueTeam: { color: "#059669", emoji: "🚐", label: "Rescue Team" },
  BlockedRoad: { color: "#ef4444", emoji: "🚧", label: "Blocked Road" },
  FloodedZone: { color: "#06b6d4", emoji: "🏞️", label: "Flooded Zone" },
  ReliefCamp: { color: "#14b8a6", emoji: "⛺", label: "Relief Camp" },
  RoadClosure: { color: "#dc2626", emoji: "⛔", label: "Road Closure" },
  Helipad: { color: "#dc2626", emoji: "🚁", label: "Helipad" },
};

const facilityIconConfigs = {
  "Panchayat Bhawan": { color: "#3b82f6", emoji: "🏛️", label: "Panchayat Bhawan" },
  "Flood Relief Camp": { color: "#06b6d4", emoji: "⛺", label: "Flood Relief Camp" },
  "Animal Shelter": { color: "#84cc16", emoji: "🐾", label: "Animal Shelter" },
  "Goverment School": { color: "#8b5cf6", emoji: "🏫", label: "Government School" },
  "Community Kitchen": { color: "#f59e0b", emoji: "🍲", label: "Community Kitchen" },
  "Government Office Buliding": { color: "#64748b", emoji: "🏢", label: "Govt Office" },
  Helipad: { color: "#dc2626", emoji: "🚁", label: "Helipad" },
  "Hospital / PHC / HC": { color: "#ef4444", emoji: "🏥", label: "Hospital/PHC" },
  "ITBP Camp": { color: "#0ea5e9", emoji: "🛡️", label: "ITBP Camp" },
  "Integrated Relief Camp": { color: "#10b981", emoji: "🏕️", label: "Integrated Relief Camp" },
};

const getBoundsFromGeoJsonCoords = (geoJson) => {
  if (!window.google?.maps) return null;
  const bounds = new window.google.maps.LatLngBounds();
  let hasPoints = false;

  const processCoords = (coords) => {
    coords.forEach(([lng, lat]) => {
      if (!isNaN(lat) && !isNaN(lng)) {
        bounds.extend({ lat, lng });
        hasPoints = true;
      }
    });
  };

  const geometry = geoJson.geometry || geoJson;
  if (!geometry) return null;

  if (geometry.type === "Polygon") {
    geometry.coordinates.forEach((ring) => processCoords(ring));
  } else if (geometry.type === "MultiPolygon") {
    geometry.coordinates.forEach((polygon) =>
      polygon.forEach((ring) => processCoords(ring))
    );
  } else if (geometry.type === "Feature") {
    return getBoundsFromGeoJsonCoords(geometry.geometry);
  } else if (geometry.type === "FeatureCollection") {
    geometry.features.forEach((f) => {
      const fb = getBoundsFromGeoJsonCoords(f);
      if (fb) bounds.union(fb);
    });
  }

  return hasPoints ? bounds : null;
};

const MapLegend = ({
  mapRef,
  isInfoWindowOpen,
  incidentMarkerData,
  supportPointsData,
  mapEvents,
  facilityCoordinates,
  selectedFacilities,
  bmskdetails,
  panchayatIncidentData,
  filteredFacilities,
  blockLayers,
  incidentBlockIds,
  panchayatLayers,
  selectedPanchayatIds,
  onFilterChange,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeItem, setActiveItem] = useState(null);

  const [visibilityState, setVisibilityState] = useState({});
  const [showAll, setShowAll] = useState(true);

  const effectiveExpanded = isInfoWindowOpen ? false : isExpanded;

  // Build legend items (unchanged logic)
  const legendItems = useMemo(() => {
    const items = [];

    if (incidentMarkerData?.basicinfo?.length > 0) {
      const seenCategories = new Set();

      incidentMarkerData.basicinfo.forEach((info) => {
        try {
          const gisconfig = JSON.parse(info.gisconfig);
          if (gisconfig?.incidentcenter?.length >= 2) {
            const cat = info.cat || "DEFAULT";
            if (!seenCategories.has(cat)) {
              seenCategories.add(cat);
              const config = disasterIconConfigs[cat] || disasterIconConfigs.DEFAULT;
              const lat = gisconfig.incidentcenter[1];
              const lng = gisconfig.incidentcenter[0];
              items.push({
                type: "incident",
                key: `incident-${cat}`,
                emoji: config.icon,
                color: config.color,
                label: `Incident - ${config.label}`,
                borderColor: config.color,
                positions: !isNaN(lat) && !isNaN(lng) ? [[lat, lng]] : [],
              });
            }
          }
        } catch (e) {}
      });

      if (panchayatIncidentData?.length > 0 && !incidentMarkerData?.basicinfo?.[0]?.gisconfig) {
        const positions = [];
        panchayatIncidentData.forEach((incident) => {
          const match = incidentMarkerData?.regions?.find((r) => r.gis_id === parseInt(incident.gisId));
          if (match && incident.latitude && incident.longitude && !isNaN(incident.latitude) && !isNaN(incident.longitude)) {
            positions.push([parseFloat(incident.latitude), parseFloat(incident.longitude)]);
          }
        });

        if (positions.length > 0) {
          const cat = incidentMarkerData.basicinfo[0]?.cat || "DEFAULT";
          if (!seenCategories.has(cat)) {
            seenCategories.add(cat);
            const config = disasterIconConfigs[cat] || disasterIconConfigs.DEFAULT;
            items.push({ type: "incident", key: `incident-panchayat-${cat}`, emoji: config.icon, color: config.color, label: `Incident - ${config.label}`, borderColor: config.color, positions });
          }
        }
      }
    }

    if (supportPointsData?.length > 0) {
      const groups = {};
      supportPointsData.forEach((point) => {
        if (point.latitude && point.longitude && !isNaN(point.latitude) && !isNaN(point.longitude)) {
          if (!groups[point.layerType]) groups[point.layerType] = [];
          groups[point.layerType].push([parseFloat(point.latitude), parseFloat(point.longitude)]);
        }
      });

      Object.entries(groups).forEach(([layerType, positions]) => {
        const config = supportPointIconConfigs[layerType];
        if (config) {
          const existingIndex = items.findIndex((item) => item.label.includes(config.label) || (layerType === "Helipad" && item.label.includes("Helipad")));
          if (existingIndex !== -1) {
            items[existingIndex].positions = [...items[existingIndex].positions, ...positions];
            items[existingIndex].label = `${config.label} (${items[existingIndex].positions.length})`;
            items[existingIndex].key = `merged-${layerType}`;
          } else {
            items.push({ type: "support", key: `support-${layerType}`, emoji: config.emoji, color: config.color, label: `${config.label} (${positions.length})`, borderColor: config.color, positions });
          }
        }
      });
    }

    const validEvents = mapEvents?.filter((e) => { const lat = parseFloat(e?.position?.[0]); const lng = parseFloat(e?.position?.[1]); return !isNaN(lat) && !isNaN(lng); });
    if (validEvents?.length > 0) {
      items.push({ type: "event", key: "event-marker", emoji: "⚠️", color: "#cc0000", label: `Events (${validEvents.length})`, borderColor: "#cc0000", positions: validEvents.map((e) => [parseFloat(e.position[0]), parseFloat(e.position[1])]) });
    }

    const facilityGroups = {};
    if (facilityCoordinates?.length > 0) {
      facilityCoordinates.forEach((f) => { const fType = normalizeFacilityValue(f); if (!facilityGroups[fType]) facilityGroups[fType] = []; if (f.position && f.position.length >= 2) { facilityGroups[fType].push([parseFloat(f.position[0]), parseFloat(f.position[1])]); } });
    }
    if (selectedFacilities?.length > 0) {
      selectedFacilities.forEach((f) => { if (typeof f.latitude === "number" && typeof f.longitude === "number" && !isNaN(f.latitude) && !isNaN(f.longitude)) { const fType = normalizeFacilityValue(f); if (!facilityGroups[fType]) facilityGroups[fType] = []; facilityGroups[fType].push([f.latitude, f.longitude]); } });
    }
    if (filteredFacilities?.length > 0) {
      filteredFacilities.forEach((f) => { const fType = normalizeFacilityValue(f); if (!facilityGroups[fType]) facilityGroups[fType] = []; if (!isNaN(f.latitude) && !isNaN(f.longitude)) { facilityGroups[fType].push([parseFloat(f.latitude), parseFloat(f.longitude)]); } });
    }

    Object.entries(facilityGroups).forEach(([fType, positions]) => {
      const config = facilityIconConfigs[fType];
      if (config && positions.length > 0) {
        const existingIndex = items.findIndex((item) => item.label.includes(config.label) || (fType === "Helipad" && item.label.includes("Helipad")));
        if (existingIndex !== -1) {
          items[existingIndex].positions = [...items[existingIndex].positions, ...positions];
          items[existingIndex].label = `${config.label} (${items[existingIndex].positions.length})`;
          items[existingIndex].key = `merged-${fType}`;
        } else {
          items.push({ type: "facility", key: `facility-merged-${fType}`, emoji: config.emoji, color: config.color, label: `${config.label} (${positions.length})`, borderColor: config.color, positions });
        }
      }
    });

    if (bmskdetails?.length > 0) {
      const positions = bmskdetails.filter((s) => !isNaN(s.latitude) && !isNaN(s.longitude)).map((s) => [parseFloat(s.latitude), parseFloat(s.longitude)]);
      items.push({ type: "bmsk", key: "bmsk-weather", emoji: "📍", color: "#f9385b", label: `LMSK Stations (${bmskdetails.length})`, borderColor: "#f9385b", isBmsk: true, positions });
    }

    if (incidentMarkerData?.regions?.length > 0) {
      const fallbackPositions = [];
      if (panchayatIncidentData?.length > 0) { panchayatIncidentData.forEach((p) => { if (p.latitude && p.longitude && !isNaN(p.latitude) && !isNaN(p.longitude)) { fallbackPositions.push([parseFloat(p.latitude), parseFloat(p.longitude)]); } }); }
      if (fallbackPositions.length === 0 && incidentMarkerData.basicinfo?.length > 0) { incidentMarkerData.basicinfo.forEach((info) => { try { const gisconfig = JSON.parse(info.gisconfig); if (gisconfig?.incidentcenter?.length >= 2) { fallbackPositions.push([gisconfig.incidentcenter[1], gisconfig.incidentcenter[0]]); } } catch (e) {} }); }

      items.push({ type: "region", key: "region-panchayat", emoji: null, color: "#3B82F6", label: "Affected Block", borderColor: "#2563EB", isRegionColor: true, fillColor: "rgba(59, 130, 246, 0.25)", positions: fallbackPositions, geoJsonData: panchayatLayers && selectedPanchayatIds?.length > 0 ? { layers: panchayatLayers, ids: selectedPanchayatIds.map(String) } : null, geoJsonMaxZoom: 10 });
      items.push({ type: "region", key: "region-block", emoji: null, color: "#22c55e", label: "Affected District", borderColor: "#22c55e", isRegionColor: true, fillColor: "rgba(74, 222, 128, 0.4)", positions: fallbackPositions, geoJsonData: blockLayers && incidentBlockIds?.length > 0 ? { layers: blockLayers, ids: incidentBlockIds.map(String) } : null, geoJsonMaxZoom: 9 });
    }

    return items;
  }, [incidentMarkerData, supportPointsData, mapEvents, facilityCoordinates, selectedFacilities, bmskdetails, panchayatIncidentData, filteredFacilities, blockLayers, incidentBlockIds, panchayatLayers, selectedPanchayatIds]);

  const extractBaseType = (key) => key.replace(/^support-/, "").replace(/^facility-merged-/, "").replace(/^merged-/, "").replace(/^incident-panchayat-/, "").replace(/^incident-/, "");

  const isRelatedKeyHidden = (baseType, state) => {
    return [`support-${baseType}`, `merged-${baseType}`, `facility-merged-${baseType}`].some((k) => state[k] === false);
  };

  const legendItemKeys = useMemo(() => legendItems.map((i) => i.key).sort().join(","), [legendItems]);

  useEffect(() => {
    const initialState = {};
    legendItems.forEach((item) => {
      const isIncident = item.type === "incident" || item.key.startsWith("incident-");
      const isRegion = item.type === "region" || item.key === "region-panchayat" || item.key === "region-block";
      if (isIncident || isRegion) { initialState[item.key] = true; }
      else if (visibilityState[item.key] !== undefined) { initialState[item.key] = visibilityState[item.key]; }
      else {
        const baseType = extractBaseType(item.key);
        const relatedHidden = isRelatedKeyHidden(baseType, visibilityState);
        initialState[item.key] = relatedHidden || !showAll ? false : true;
      }
    });
    setVisibilityState((prev) => ({ ...prev, ...initialState }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legendItemKeys]);

  useEffect(() => {
    if (onFilterChange) onFilterChange(visibilityState);
  }, [visibilityState, onFilterChange]);

  const handleShowAllToggle = useCallback(() => {
    const newShowAll = !showAll;
    setShowAll(newShowAll);
    const newState = {};
    legendItems.forEach((item) => {
      if (item.key.startsWith("incident-") || item.key === "region-panchayat" || item.key === "region-block") { newState[item.key] = true; }
      else { newState[item.key] = newShowAll; }
    });
    setVisibilityState(newState);
  }, [showAll, legendItems]);

  const handleCheckboxToggle = useCallback((key, e) => {
    e.stopPropagation();
    if (key.startsWith("incident-") || key === "region-panchayat" || key === "region-block") return;
    setVisibilityState((prev) => {
      const newState = { ...prev, [key]: !prev[key] };
      const nonLockedItems = legendItems.filter((item) => !item.key.startsWith("incident-") && item.key !== "region-panchayat" && item.key !== "region-block");
      setShowAll(nonLockedItems.every((item) => newState[item.key]));
      return newState;
    });
  }, [legendItems]);

  const handleLegendItemClick = useCallback((item) => {
    if (!mapRef) return;
    setActiveItem(item.key);
    setTimeout(() => setActiveItem(null), 2000);

    if (item.geoJsonData && window.google?.maps) {
      const { layers, ids } = item.geoJsonData;
      if (layers && ids?.length > 0) {
        const combinedBounds = new window.google.maps.LatLngBounds();
        let hasValidBounds = false;
        ids.forEach((id) => { const geoJson = layers[id]; if (geoJson) { const layerBounds = getBoundsFromGeoJsonCoords(geoJson); if (layerBounds) { combinedBounds.union(layerBounds); hasValidBounds = true; } } });
        if (hasValidBounds && !combinedBounds.isEmpty()) {
          mapRef.fitBounds(combinedBounds, { top: 60, right: 60, bottom: 60, left: 60 });
          const maxZoom = item.geoJsonMaxZoom || 12;
          window.google.maps.event.addListenerOnce(mapRef, "idle", () => { if (mapRef.getZoom() > maxZoom) mapRef.setZoom(maxZoom); });
          return;
        }
      }
    }

    if (!item.positions || item.positions.length === 0 || !window.google?.maps) return;

    if (item.positions.length === 1) {
      const [lat, lng] = item.positions[0];
      mapRef.panTo({ lat, lng });
      mapRef.setZoom(14);
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      item.positions.forEach(([lat, lng]) => bounds.extend({ lat, lng }));
      mapRef.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
      window.google.maps.event.addListenerOnce(mapRef, "idle", () => { if (mapRef.getZoom() > 15) mapRef.setZoom(15); });
    }
  }, [mapRef]);

  if (legendItems.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "10px",
        left: "50px",
        backgroundColor: "rgba(255, 255, 255, 0.97)",
        borderRadius: "12px",
        boxShadow: "0 4px 24px rgba(30,58,95,0.12), 0 1px 4px rgba(0,0,0,0.06)",
        border: "1px solid rgba(30,58,95,0.08)",
        minWidth: effectiveExpanded ? "250px" : "auto",
        maxWidth: "340px",
        maxHeight: "420px",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
        fontFamily: "'Segoe UI', -apple-system, sans-serif",
        opacity: isInfoWindowOpen ? 0.25 : 1,
        pointerEvents: isInfoWindowOpen ? "none" : "auto",
        backdropFilter: "blur(12px)",
      }}
    >
      {/* Header */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          background: "linear-gradient(135deg, #1a3f6f 0%, #245a8d 100%)",
          color: "white",
          cursor: "pointer",
          borderRadius: effectiveExpanded ? "12px 12px 0 0" : "12px",
          fontSize: "13px",
          fontWeight: "600",
          userSelect: "none",
          letterSpacing: "0.02em",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          🗺️ Legend
          <span
            style={{
              backgroundColor: "rgba(255,255,255,0.2)",
              borderRadius: "12px",
              padding: "2px 8px",
              fontSize: "10px",
              fontWeight: "600",
            }}
          >
            {legendItems.length}
          </span>
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {effectiveExpanded && (
            <span
              title="Click any label to zoom to that layer"
              style={{ fontSize: "11px", opacity: 0.85, cursor: "default", userSelect: "none", display: "flex", alignItems: "center" }}
            >
              <Search color="white" size={13} />
            </span>
          )}
          {effectiveExpanded ? <FaChevronDown size={11} /> : <FaChevronUp size={11} />}
        </span>
      </div>

      {/* Items */}
      {effectiveExpanded && (
        <div style={{ overflowY: "auto", maxHeight: "360px" }}>
          {/* Show All */}
          {/* <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              padding: "10px 14px",
              borderBottom: "1px solid rgba(30,58,95,0.06)",
              backgroundColor: "#f8fafd",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={handleShowAllToggle}
          >
            <input
              type="checkbox"
              checked={showAll}
              onChange={handleShowAllToggle}
              onClick={(e) => e.stopPropagation()}
              style={{ width: "15px", height: "15px", cursor: "pointer", accentColor: "#2a7de1" }}
            />
            <span style={{ fontSize: "12px", fontWeight: "700", color: "#1a2a3a", flex: 1 }}>Show All</span>
          </div> */}

          {/* Individual Items */}
          <div style={{ padding: "6px 8px" }}>
            {legendItems.map((item) => {
              const hasPositions = (item.positions && item.positions.length > 0) || item.geoJsonData;
              const isActive = activeItem === item.key;
              const isVisible = visibilityState[item.key] !== false;
              const isLocked = item.key.startsWith("incident-") || item.key === "region-panchayat" || item.key === "region-block";

              return (
                <div
                  key={item.key}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 8px",
                    borderRadius: "8px",
                    marginBottom: "2px",
                    cursor: "default",
                    transition: "all 0.15s ease",
                    backgroundColor: isActive ? `${item.borderColor || item.color}10` : "transparent",
                    border: isActive ? `1.5px solid ${item.borderColor || item.color}30` : "1.5px solid transparent",
                    opacity: isVisible ? 1 : 0.4,
                  }}
                  onMouseEnter={(e) => { if (hasPositions && isVisible) e.currentTarget.style.backgroundColor = isActive ? `${item.borderColor || item.color}15` : "rgba(42,125,225,0.04)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isActive ? `${item.borderColor || item.color}10` : "transparent"; }}
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={(e) => handleCheckboxToggle(item.key, e)}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isLocked}
                    title={isLocked ? "Always shown — this layer can't be hidden" : undefined}
                    style={{
                      width: "14px",
                      height: "14px",
                      cursor: isLocked ? "not-allowed" : "pointer",
                      accentColor: item.borderColor || item.color,
                      flexShrink: 0,
                      opacity: isLocked ? 0.6 : 1,
                    }}
                  />

                  {item.isRegionColor ? (
                    <div style={{ width: "22px", height: "12px", borderRadius: "3px", border: `2px solid ${item.borderColor}`, backgroundColor: item.fillColor, flexShrink: 0 }} />
                  ) : item.isBmsk ? (
                    <div style={{ width: "22px", height: "22px", borderRadius: "50%", backgroundColor: item.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <svg width="12" height="12" viewBox="0 0 128 128" fill="none"><path fill="#ffffff" d="M64 30a24 24 0 0 0-24 24c0 18 24 40 24 40s24-22 24-40a24 24 0 0 0-24-24zm0 36a12 12 0 1 1 0-24 12 12 0 0 1 0 24z" /></svg>
                    </div>
                  ) : (
                    <div
                      style={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "50%",
                        border: item.type === "incident" || item.key.startsWith("incident-") ? `2px solid #DC2626` : `2px solid ${item.borderColor}`,
                        backgroundColor: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "13px",
                        flexShrink: 0,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                      }}
                    >
                      {item.emoji}
                    </div>
                  )}

                  <span
                    onClick={() => hasPositions && isVisible && handleLegendItemClick(item)}
                    style={{
                      fontSize: "11.5px",
                      color: "#1a2a3a",
                      fontWeight: "500",
                      lineHeight: "1.2",
                      flex: 1,
                      cursor: hasPositions && isVisible ? "pointer" : "default",
                    }}
                    title={hasPositions && isVisible ? `Click to zoom to ${item.label}` : item.label}
                  >
                    {item.label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapLegend;